import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import './AnalyticsDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTO_REFRESH_INTERVAL_MS = 60 * 1000;
const AUTO_REFRESH_INTERVAL_LABEL = '60s';

type RangeKey = '7d' | '30d' | '90d' | 'lifetime';

interface AnalyticsApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: Record<string, unknown>;
  error?: string;
}

interface DashboardSummary {
  totalViews: number;
  totalEngagement: number;
  totalWatchMinutes: number;
  totalSubscribersGained: number;
  totalPosts: number;
  engagementRate: number;
  likes: number;
  comments: number;
  shares: number;
  activeAccounts: number;
  supportedAccounts: number;
  unsupportedAccounts: number;
  staleAccounts: number;
  syncedAccounts: number;
  latestFetchedAt: string | null;
}

interface DashboardOverviewAccount {
  id: string;
  platform: string;
  platformAccountId: string | null;
  accountName: string | null;
  status: string;
  supported: boolean;
  isStale: boolean;
  lastInsightSyncAt: string | null;
  views: number;
  engagement: number;
  watchMinutes: number;
  subscribersGained: number;
}

interface DashboardOverview {
  summary: DashboardSummary;
  accounts: DashboardOverviewAccount[];
}

interface DashboardVideoPerformance {
  views: number;
  likes: number;
  comments: number;
  shares: number;
  engagement: number;
  engagementRate: number;
  watchMinutes: number;
  avgViewDurationSeconds: number;
  subscribersGained: number;
}

interface DashboardVideoRow {
  id: string;
  publishRequestId: string;
  platform: string;
  title: string;
  projectId: string | null;
  projectName: string | null;
  linkedSocialAccountId: string | null;
  accountName: string | null;
  platformPostId: string | null;
  postUrl: string | null;
  publishedAt: string | null;
  fetchedAt: string | null;
  performance: DashboardVideoPerformance;
}

interface DashboardVideoList {
  count: number;
  total: number;
  rows: DashboardVideoRow[];
}

interface DashboardPlatformComparisonRow {
  platform: string;
  accountCount: number;
  postCount: number;
  viewsSum: number;
  watchTimeMinutesSum: number;
  engagementTotalSum: number;
  ctrWeighted: number;
  avgViewDurationSecondsWeighted: number;
  engagementRateWeighted: number;
  subscribersGainedSum: number;
}

interface DashboardAccountRow {
  linkedSocialAccountId: string;
  platform: string;
  platformAccountId: string | null;
  accountName: string | null;
  postCount: number;
  viewsSum: number;
  watchTimeMinutesSum: number;
  engagementTotalSum: number;
  engagementRateWeighted: number;
  subscribersGainedSum: number;
  lastFetchedAt: string | null;
  isStale: boolean;
}

interface DashboardCountryRow {
  countryCode: string;
  countryName: string;
  views: number;
  watchTimeMinutes: number;
  engagementTotal: number;
  engagementRate: number;
  postCount: number;
}

interface DashboardCountryInsights {
  availability: 'available' | 'unavailable_for_provider';
  rows: DashboardCountryRow[];
}

interface DashboardDataQuality {
  totalRows: number;
  completeRows: number;
  partialRows: number;
  emptyRows: number;
  apiSourceRows: number;
  cacheSourceRows: number;
  staleRows: number;
  latestFetchedAt: string | null;
}

interface ManualSyncLinkedAccount {
  id: string;
  provider: string;
  status: 'connected' | 'syncing' | 'needs_reconnect' | 'disconnected';
}

interface MeResponse {
  linkedAccounts?: ManualSyncLinkedAccount[];
}

function formatPlatform(platform: string): string {
  if (platform === 'youtube') return 'YouTube';
  if (platform === 'facebook') return 'Facebook';
  if (platform === 'linkedin') return 'LinkedIn';
  if (platform === 'instagram') return 'Instagram';
  if (platform === 'tiktok') return 'TikTok';
  if (platform === 'twitter') return 'X';
  if (!platform) return 'Unknown';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function toFiniteNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function formatRangeLabel(range: RangeKey): string {
  if (range === '7d') return 'Last 7 Days';
  if (range === '30d') return 'Last 30 Days';
  if (range === '90d') return 'Last 90 Days';
  return 'Lifetime';
}

function normalizeProvider(provider: string): string {
  if (provider === 'google') return 'youtube';
  if (provider === 'x') return 'twitter';
  return provider;
}

async function fetchEnvelope<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    signal,
  });

  const payload = (await response.json().catch(() => ({
    success: false,
    error: 'Failed to parse server response',
  }))) as Partial<AnalyticsApiEnvelope<T>>;

  if (!response.ok || payload.success === false || payload.data === undefined) {
    throw new Error(payload.error || `Failed request: ${response.status}`);
  }

  return payload.data as T;
}

const EMPTY_SUMMARY: DashboardSummary = {
  totalViews: 0,
  totalEngagement: 0,
  totalWatchMinutes: 0,
  totalSubscribersGained: 0,
  totalPosts: 0,
  engagementRate: 0,
  likes: 0,
  comments: 0,
  shares: 0,
  activeAccounts: 0,
  supportedAccounts: 0,
  unsupportedAccounts: 0,
  staleAccounts: 0,
  syncedAccounts: 0,
  latestFetchedAt: null,
};

const AnalyticsDashboard: React.FC = () => {
  const [range, setRange] = useState<RangeKey>('30d');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(true);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState<boolean>(false);
  const [isSyncingInsights, setIsSyncingInsights] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [videos, setVideos] = useState<DashboardVideoList>({ count: 0, total: 0, rows: [] });
  const [platformComparisons, setPlatformComparisons] = useState<DashboardPlatformComparisonRow[]>([]);
  const [accountRows, setAccountRows] = useState<DashboardAccountRow[]>([]);
  const [countryInsights, setCountryInsights] = useState<DashboardCountryInsights | null>(null);
  const [dataQuality, setDataQuality] = useState<DashboardDataQuality | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef<boolean>(false);
  const inflightFetchRef = useRef<boolean>(false);
  const pendingFetchRef = useRef<{ silent?: boolean } | null>(null);

  const fetchDashboard = useCallback(async function fetchDashboardRequest(signal?: AbortSignal, options?: { silent?: boolean }): Promise<void> {
    if (inflightFetchRef.current) {
      const requestedSilent = Boolean(options?.silent);
      const existingPending = pendingFetchRef.current;
      // Keep one queued refresh while preserving highest priority (blocking > silent).
      pendingFetchRef.current = {
        silent: existingPending ? existingPending.silent && requestedSilent : requestedSilent,
      };
      return;
    }

    inflightFetchRef.current = true;
    const isSilent = Boolean(options?.silent);
    const shouldShowBlockingLoader = !isSilent || !hasLoadedOnceRef.current;

    if (shouldShowBlockingLoader) {
      setLoading(true);
    }
    if (isSilent) {
      setIsAutoRefreshing(true);
    }

    setError(null);

    try {
      const baseParams = new URLSearchParams();
      baseParams.set('rangeKey', range);
      if (platformFilter !== 'all') {
        baseParams.set('platform', platformFilter);
      }

      const scopedParams = new URLSearchParams(baseParams);
      if (accountFilter !== 'all') {
        scopedParams.set('linkedSocialAccountId', accountFilter);
      }

      const videosParams = new URLSearchParams(scopedParams);
      videosParams.set('limit', '100');
      videosParams.set('offset', '0');

      const countriesParams = new URLSearchParams(scopedParams);
      countriesParams.set('limit', '8');

      const accountsParams = new URLSearchParams(baseParams);
      accountsParams.set('limit', '500');

      const [
        overviewData,
        videosData,
        platformData,
        accountsData,
        countryData,
        qualityData,
      ] = await Promise.all([
        fetchEnvelope<DashboardOverview>(`${API_BASE}/api/analytics/dashboard/overview?${scopedParams.toString()}`, signal),
        fetchEnvelope<DashboardVideoList>(`${API_BASE}/api/analytics/dashboard/videos?${videosParams.toString()}`, signal),
        fetchEnvelope<DashboardPlatformComparisonRow[]>(`${API_BASE}/api/analytics/dashboard/platforms/compare?${scopedParams.toString()}`, signal),
        fetchEnvelope<DashboardAccountRow[]>(`${API_BASE}/api/analytics/dashboard/accounts?${accountsParams.toString()}`, signal),
        fetchEnvelope<DashboardCountryInsights>(`${API_BASE}/api/analytics/dashboard/countries?${countriesParams.toString()}`, signal),
        fetchEnvelope<DashboardDataQuality>(`${API_BASE}/api/analytics/dashboard/data-quality?${scopedParams.toString()}`, signal),
      ]);

      setOverview(overviewData);
      setVideos(videosData);
      setPlatformComparisons(platformData);
      setAccountRows(accountsData);
      setCountryInsights(countryData);
      setDataQuality(qualityData);
      setLastLoadedAt(new Date().toISOString());
      hasLoadedOnceRef.current = true;
    } catch (err: unknown) {
      if ((err as { name?: string }).name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load analytics dashboard');
    } finally {
      inflightFetchRef.current = false;
      if (shouldShowBlockingLoader) {
        setLoading(false);
      }
      if (isSilent) {
        setIsAutoRefreshing(false);
      }

      const queuedRequest = pendingFetchRef.current;
      pendingFetchRef.current = null;
      if (queuedRequest) {
        void fetchDashboardRequest(undefined, queuedRequest);
      }
    }
  }, [accountFilter, platformFilter, range]);

  const waitForInflightDashboardFetch = useCallback(async (): Promise<void> => {
    if (!inflightFetchRef.current) {
      return;
    }
    await new Promise<void>((resolve) => {
      const tick = () => {
        if (!inflightFetchRef.current) {
          resolve();
          return;
        }
        window.setTimeout(tick, 50);
      };
      tick();
    });
  }, []);

  const handleManualRefresh = useCallback(async (): Promise<void> => {
    if (isSyncingInsights) {
      return;
    }

    setIsSyncingInsights(true);
    setError(null);

    try {
      const targetAccountIdsFromOverview = accountFilter !== 'all'
        ? [accountFilter]
        : Array.from(
          new Set(
            (overview?.accounts || [])
              .filter((account) => account.supported)
              .map((account) => account.id)
          )
        );

      let targetAccountIds = targetAccountIdsFromOverview;

      if (targetAccountIds.length === 0) {
        const meResponse = await fetch(`${API_BASE}/api/me`, {
          method: 'GET',
          credentials: 'include',
        });
        const mePayload = (await meResponse.json().catch(() => ({}))) as MeResponse;

        if (meResponse.ok && Array.isArray(mePayload.linkedAccounts)) {
          targetAccountIds = mePayload.linkedAccounts
            .filter((account) => account.status === 'connected' || account.status === 'syncing')
            .filter((account) => normalizeProvider(account.provider) === 'youtube')
            .map((account) => account.id);
        }
      }

      if (targetAccountIds.length === 0) {
        throw new Error('No connected YouTube account found. Connect YouTube in Social Accounts first.');
      }

      if (targetAccountIds.length > 0) {
        const baselineLatestFetchedAt = overview?.summary.latestFetchedAt || null;
        const syncResults = await Promise.allSettled(
          targetAccountIds.map(async (accountId) => {
            const response = await fetch(`${API_BASE}/api/accounts/${accountId}/insights/sync`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                force: true,
                reason: 'dashboard_manual_refresh',
              }),
            });
            const payload = (await response.json().catch(() => ({}))) as { error?: string };
            if (!response.ok) {
              throw new Error(payload.error || `Sync failed (${response.status})`);
            }
          })
        );

        const failedCount = syncResults.reduce((count, result) => {
          return result.status === 'rejected' ? count + 1 : count;
        }, 0);

        if (failedCount === targetAccountIds.length) {
          const firstFailure = syncResults.find((result) => result.status === 'rejected');
          const failureReason = firstFailure && firstFailure.status === 'rejected'
            ? (firstFailure.reason instanceof Error ? firstFailure.reason.message : String(firstFailure.reason))
            : '';
          throw new Error(failureReason || 'Unable to queue insight sync for selected account(s).');
        }

        if (failedCount > 0) {
          setError(
            `Queued sync for ${targetAccountIds.length - failedCount}/${targetAccountIds.length} accounts. Some sync requests failed.`
          );
        }

        // queueAccountSync is async/fire-and-forget server-side. Poll overview briefly
        // so first-time insight rows have time to land before we rerender.
        const scopedParams = new URLSearchParams();
        scopedParams.set('rangeKey', range);
        if (platformFilter !== 'all') {
          scopedParams.set('platform', platformFilter);
        }
        if (accountFilter !== 'all') {
          scopedParams.set('linkedSocialAccountId', accountFilter);
        }

        const maxPollAttempts = 8;
        for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, 1500);
          });

          try {
            const latestOverview = await fetchEnvelope<DashboardOverview>(
              `${API_BASE}/api/analytics/dashboard/overview?${scopedParams.toString()}`
            );
            const nextLatestFetchedAt = latestOverview.summary.latestFetchedAt;
            if (nextLatestFetchedAt && nextLatestFetchedAt !== baselineLatestFetchedAt) {
              break;
            }
          } catch {
            // Ignore transient polling errors and proceed to final refresh.
          }
        }
      }

      await waitForInflightDashboardFetch();
      await fetchDashboard();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to trigger analytics sync');
    } finally {
      setIsSyncingInsights(false);
    }
  }, [
    accountFilter,
    fetchDashboard,
    isSyncingInsights,
    overview?.summary.latestFetchedAt,
    overview?.accounts,
    platformFilter,
    range,
    waitForInflightDashboardFetch,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchDashboard(controller.signal);
    return () => controller.abort();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!autoRefreshEnabled) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchDashboard(undefined, { silent: true });
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [autoRefreshEnabled, fetchDashboard]);

  useEffect(() => {
    if (accountFilter === 'all') return;
    const exists = accountRows.some((row) => row.linkedSocialAccountId === accountFilter);
    if (!exists) {
      setAccountFilter('all');
    }
  }, [accountFilter, accountRows]);

  const topVideos = useMemo(() => {
    return [...videos.rows]
      .sort((left, right) => right.performance.views - left.performance.views || right.performance.engagement - left.performance.engagement)
      .slice(0, 10);
  }, [videos.rows]);

  const dashboardTotals = useMemo<DashboardSummary>(() => {
    const summary = overview?.summary;
    if (!summary) {
      return EMPTY_SUMMARY;
    }

    const normalized: DashboardSummary = {
      ...EMPTY_SUMMARY,
      ...summary,
      totalViews: toFiniteNumber(summary.totalViews),
      totalEngagement: toFiniteNumber(summary.totalEngagement),
      totalWatchMinutes: toFiniteNumber(summary.totalWatchMinutes),
      totalSubscribersGained: toFiniteNumber(summary.totalSubscribersGained),
      totalPosts: toFiniteNumber(summary.totalPosts),
      engagementRate: toFiniteNumber(summary.engagementRate),
      likes: toFiniteNumber(summary.likes),
      comments: toFiniteNumber(summary.comments),
      shares: toFiniteNumber(summary.shares),
      activeAccounts: toFiniteNumber(summary.activeAccounts),
      supportedAccounts: toFiniteNumber(summary.supportedAccounts),
      unsupportedAccounts: toFiniteNumber(summary.unsupportedAccounts),
      staleAccounts: toFiniteNumber(summary.staleAccounts),
      syncedAccounts: toFiniteNumber(summary.syncedAccounts),
      latestFetchedAt: summary.latestFetchedAt || null,
    };

    const hasBreakdown = normalized.likes + normalized.comments + normalized.shares > 0;
    if (!hasBreakdown && videos.rows.length > 0) {
      const fallbackLikes = videos.rows.reduce((sum, row) => sum + toFiniteNumber(row.performance?.likes), 0);
      const fallbackComments = videos.rows.reduce((sum, row) => sum + toFiniteNumber(row.performance?.comments), 0);
      const fallbackShares = videos.rows.reduce((sum, row) => sum + toFiniteNumber(row.performance?.shares), 0);
      normalized.likes = fallbackLikes;
      normalized.comments = fallbackComments;
      normalized.shares = fallbackShares;
      if (normalized.totalEngagement <= 0) {
        normalized.totalEngagement = fallbackLikes + fallbackComments + fallbackShares;
      }
    }

    return normalized;
  }, [overview?.summary, videos.rows]);

  const platformOptions = useMemo(() => {
    const values = Array.from(new Set(accountRows.map((account) => account.platform)));
    return values.sort();
  }, [accountRows]);

  const supportedCount = dashboardTotals.supportedAccounts;

  const countryStateMessage = useMemo(() => {
    if (!countryInsights) return 'Loading country analytics...';
    if (countryInsights.rows.length > 0) return '';
    if (countryInsights.availability === 'unavailable_for_provider') {
      return 'Country insights are unavailable for one or more selected providers. Connect supported accounts and sync fresh data.';
    }
    return 'Country analytics returned no rows for the selected filters. Expand the date range or remove account filters.';
  }, [countryInsights]);

  const largestPlatformViews = Math.max(...platformComparisons.map((row) => row.viewsSum), 1);

  const donutTotal = dashboardTotals.likes + dashboardTotals.comments + dashboardTotals.shares;
  const likePct = donutTotal > 0 ? (dashboardTotals.likes / donutTotal) * 100 : 0;
  const commentPct = donutTotal > 0 ? (dashboardTotals.comments / donutTotal) * 100 : 0;
  const sharePct = donutTotal > 0 ? (dashboardTotals.shares / donutTotal) * 100 : 0;

  const donutStyle = {
    background: donutTotal > 0
      ? `conic-gradient(
          var(--accent-green) 0% ${likePct.toFixed(2)}%,
          var(--accent-blue) ${likePct.toFixed(2)}% ${(likePct + commentPct).toFixed(2)}%,
          #F59E0B ${(likePct + commentPct).toFixed(2)}% ${(likePct + commentPct + sharePct).toFixed(2)}%
        )`
      : 'conic-gradient(rgba(148, 163, 184, 0.42) 0% 100%)',
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content analytics-dashboard-main">
        <Header />

        <section className="analytics-head">
          <div>
            <h1>Analytics</h1>
            <p className="analytics-head-subtitle">Cross-platform performance, engagement, and account health in one view.</p>
          </div>

          <div className="analytics-filters">
            <label>
              <span>Range</span>
              <select value={range} onChange={(event) => setRange(event.target.value as RangeKey)}>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </label>

            <label>
              <span>Platform</span>
              <select value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value)}>
                <option value="all">All Platforms</option>
                {platformOptions.map((platform) => (
                  <option key={platform} value={platform}>{formatPlatform(platform)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Account</span>
              <select value={accountFilter} onChange={(event) => setAccountFilter(event.target.value)}>
                <option value="all">All Accounts</option>
                {accountRows.map((account) => (
                  <option key={account.linkedSocialAccountId} value={account.linkedSocialAccountId}>
                    {account.accountName || account.platformAccountId || `${formatPlatform(account.platform)} Account`}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Auto Refresh</span>
              <select
                value={autoRefreshEnabled ? 'on' : 'off'}
                onChange={(event) => setAutoRefreshEnabled(event.target.value === 'on')}
              >
                <option value="on">On ({AUTO_REFRESH_INTERVAL_LABEL})</option>
                <option value="off">Off</option>
              </select>
            </label>

            <button
              type="button"
              className="analytics-refresh-btn"
              onClick={() => void handleManualRefresh()}
              disabled={loading || isSyncingInsights}
            >
              {isSyncingInsights ? 'Syncing…' : loading ? 'Refreshing…' : 'Sync + Refresh'}
            </button>
          </div>
        </section>

        {error ? (
          <div className="analytics-alert analytics-alert-error">{error}</div>
        ) : null}

        <section className="analytics-kpi-grid">
          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Total Views</div>
            <div className="analytics-kpi-value">{formatCompact(dashboardTotals.totalViews)}</div>
            <div className="analytics-kpi-meta">{formatNumber(dashboardTotals.totalPosts)} posts analyzed</div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Engagement</div>
            <div className="analytics-kpi-value">{formatCompact(dashboardTotals.totalEngagement)}</div>
            <div className="analytics-kpi-meta">{dashboardTotals.engagementRate.toFixed(2)}% weighted rate</div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Watch Time</div>
            <div className="analytics-kpi-value">{formatCompact(dashboardTotals.totalWatchMinutes)}m</div>
            <div className="analytics-kpi-meta">Aggregated from normalized insight metrics</div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Subscribers Gained</div>
            <div className="analytics-kpi-value">{formatCompact(dashboardTotals.totalSubscribersGained)}</div>
            <div className="analytics-kpi-meta">Across selected accounts and posts</div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Active Accounts</div>
            <div className="analytics-kpi-value">{dashboardTotals.activeAccounts}</div>
            <div className="analytics-kpi-meta">{supportedCount} supported · {dashboardTotals.unsupportedAccounts} unsupported</div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Freshness</div>
            <div className="analytics-kpi-value">{dashboardTotals.staleAccounts === 0 ? 'Healthy' : 'Needs Sync'}</div>
            <div className="analytics-kpi-meta">{dashboardTotals.staleAccounts} stale account{dashboardTotals.staleAccounts === 1 ? '' : 's'}</div>
          </article>
        </section>

        <section className="analytics-grid analytics-grid-two">
          <article className="analytics-panel analytics-panel-platforms">
            <div className="analytics-panel-header">
              <h2>Platform Performance</h2>
              <span>Views and engagement by platform</span>
            </div>

            {platformComparisons.length === 0 ? (
              <div className="analytics-empty">No platform analytics available for current filters.</div>
            ) : (
              <div className="analytics-platform-list">
                {platformComparisons.map((row) => {
                  const barWidth = Math.max(6, (row.viewsSum / largestPlatformViews) * 100);
                  return (
                    <div key={row.platform} className="analytics-platform-row">
                      <div className="analytics-platform-label">
                        <strong>{formatPlatform(row.platform)}</strong>
                        <span>{row.accountCount} account{row.accountCount === 1 ? '' : 's'} · {row.postCount} posts</span>
                      </div>

                      <div className="analytics-platform-bar-track">
                        <div className="analytics-platform-bar-fill" style={{ width: `${barWidth.toFixed(2)}%` }} />
                      </div>

                      <div className="analytics-platform-metrics">
                        <span>{formatCompact(row.viewsSum)} views</span>
                        <span>{row.engagementRateWeighted.toFixed(2)}% ER</span>
                        <span>{formatCompact(row.watchTimeMinutesSum)}m watch</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </article>

          <article className="analytics-panel analytics-panel-engagement">
            <div className="analytics-panel-header">
              <h2>Engagement Mix</h2>
              <span>Likes, comments, and shares composition</span>
            </div>

            <div className="analytics-donut-wrap">
              <div className="analytics-donut" style={donutStyle}>
                <div className="analytics-donut-center">
                  <strong>{formatCompact(dashboardTotals.totalEngagement)}</strong>
                  <span>{donutTotal > 0 ? 'Total' : 'No Data'}</span>
                </div>
              </div>

              <div className="analytics-donut-legend">
                <div>
                  <span className="analytics-dot analytics-dot-like" />
                  Likes: {formatNumber(dashboardTotals.likes)}
                </div>
                <div>
                  <span className="analytics-dot analytics-dot-comment" />
                  Comments: {formatNumber(dashboardTotals.comments)}
                </div>
                <div>
                  <span className="analytics-dot analytics-dot-share" />
                  Shares: {formatNumber(dashboardTotals.shares)}
                </div>
              </div>
            </div>
          </article>
        </section>

        <section className="analytics-grid analytics-grid-two">
          <article className="analytics-panel">
            <div className="analytics-panel-header">
              <h2>Top Videos</h2>
              <span>Highest-performing posts across selected filters</span>
            </div>

            {topVideos.length === 0 ? (
              <div className="analytics-empty">No video insight rows available yet.</div>
            ) : (
              <div className="analytics-table">
                <div className="analytics-table-head">
                  <span>Video</span>
                  <span>Platform</span>
                  <span>Views</span>
                  <span>Engagement</span>
                </div>
                {topVideos.map((video) => (
                  <a
                    key={video.id}
                    className="analytics-table-row"
                    href={video.postUrl || '#'}
                    target={video.postUrl ? '_blank' : undefined}
                    rel={video.postUrl ? 'noreferrer' : undefined}
                    onClick={(event) => {
                      if (!video.postUrl) event.preventDefault();
                    }}
                  >
                    <span>
                      <strong>{video.title || 'Untitled post'}</strong>
                      <em>{video.projectName || video.accountName || formatPlatform(video.platform)}</em>
                    </span>
                    <span>{formatPlatform(video.platform)}</span>
                    <span>{formatCompact(video.performance.views)}</span>
                    <span>{formatCompact(video.performance.engagement)} ({video.performance.engagementRate.toFixed(1)}%)</span>
                  </a>
                ))}
              </div>
            )}
          </article>

          <article className="analytics-panel">
            <div className="analytics-panel-header">
              <h2>Views by Country</h2>
              <span>Geo distribution from normalized country metrics</span>
            </div>

            {countryInsights && countryInsights.rows.length > 0 ? (
              <div className="analytics-country-list">
                {countryInsights.rows.map((country) => (
                  <div key={country.countryCode} className="analytics-country-row">
                    <div className="analytics-country-row-main">
                      <strong>{country.countryName}</strong>
                      <span>{country.countryCode}</span>
                    </div>
                    <div className="analytics-country-row-metrics">
                      <span>{formatCompact(country.views)} views</span>
                      <span>{country.engagementRate.toFixed(2)}% ER</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="analytics-country-placeholder">
                <div className="analytics-country-map" aria-hidden="true">
                  <div className="analytics-country-pulse analytics-country-pulse-1" />
                  <div className="analytics-country-pulse analytics-country-pulse-2" />
                  <div className="analytics-country-pulse analytics-country-pulse-3" />
                </div>
                <p>{countryStateMessage}</p>
              </div>
            )}
          </article>
        </section>

        <section className="analytics-grid analytics-grid-two">
          <article className="analytics-panel">
            <div className="analytics-panel-header">
              <h2>Data Quality</h2>
              <span>Sync confidence and freshness diagnostics</span>
            </div>

            <div className="analytics-quality-grid">
              <div>
                <span>Synced Accounts</span>
                <strong>{dashboardTotals.syncedAccounts}</strong>
              </div>
              <div>
                <span>Stale Accounts</span>
                <strong>{dashboardTotals.staleAccounts}</strong>
              </div>
              <div>
                <span>Complete Rows</span>
                <strong>{dataQuality ? dataQuality.completeRows : 0}</strong>
              </div>
              <div>
                <span>Partial Rows</span>
                <strong>{dataQuality ? dataQuality.partialRows : 0}</strong>
              </div>
            </div>
          </article>

          <article className="analytics-panel">
            <div className="analytics-panel-header">
              <h2>Sync Signals</h2>
              <span>Backend insight timestamps and stale detection</span>
            </div>

            <div className="analytics-sync-list">
              <div>
                <span>Latest Insight Fetch</span>
                <strong>{dashboardTotals.latestFetchedAt ? new Date(dashboardTotals.latestFetchedAt).toLocaleString() : 'No sync yet'}</strong>
              </div>
              <div>
                <span>Range Window</span>
                <strong>{formatRangeLabel(range)}</strong>
              </div>
              <div>
                <span>Auto Refresh</span>
                <strong>
                  {autoRefreshEnabled ? `Enabled (${AUTO_REFRESH_INTERVAL_LABEL})` : 'Paused'}
                  {isAutoRefreshing ? ' · updating' : ''}
                </strong>
              </div>
              <div>
                <span>Scope</span>
                <strong>{platformFilter === 'all' ? 'All Platforms' : formatPlatform(platformFilter)}</strong>
              </div>
              <div>
                <span>Source Mix</span>
                <strong>
                  API {dataQuality ? dataQuality.apiSourceRows : 0}
                  {' · '}
                  Cache {dataQuality ? dataQuality.cacheSourceRows : 0}
                </strong>
              </div>
              <div>
                <span>Last Loaded</span>
                <strong>{lastLoadedAt ? new Date(lastLoadedAt).toLocaleTimeString() : '—'}</strong>
              </div>
            </div>
          </article>
        </section>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
